<template lang="pug">
native-app-list-item(@click="open" no-icon)
  .item-thumbnail
    DynamicMedia(:config="{ src: thumbnail || placeholder, width: 500, height: 500 }")
  .details
    .header
      DynamicMedia.item-label(
        v-if="reservedLabels[lowerCaseLabel]"
        :config="{ src: reservedLabels[lowerCaseLabel] }"
      )
      .item-label(v-else) {{ item.type === 'bin' ? 'Back In Stock!' : item.title }}
      .item-date {{ item.date || getNotificationDate(item.createdAt) }}
        span.item-unread(v-if="!item.read")
    .item-description(v-if="['order', 'rss'].includes(item.type)") {{ item.orderId }}
    .item-description(v-else :class="{'font-weight-bold': !item.read}") {{ item.inboxTitle }}
</template>

<script setup lang="ts">
import { messages }                   from 'Src/js/app-bridge/messages'
import { getDateObjectFromTimestamp } from 'Src/js/helpers/date'
import { useProductStore }            from 'Src/js/stores/productStore'
import NativeAppListItem              from 'Src/js/pages/_rg-app/components/native-app-list-item.vue'
import { computed, onMounted, ref } from 'vue'
import type { IPushNotification } from '../notificationTypes'
import { useRogue } from '@/js/services/Rogue'

const props = defineProps<{
  item: IPushNotification,
}>()

const emit = defineEmits(['open'])

const rogue = useRogue()
const productStore = useProductStore()

const placeholder = '/Rogue%20App%20Dashboard/placeholder-new_y5q4bz.png'
const reservedLabels = {
  appexclusive : '/Rogue%20App%20Dashboard/Inbox/app-exclusive_fuzn1j.svg',
  mbfexclusive : '/Rogue%20App%20Dashboard/Inbox/mbf-exclusive_kac76g.svg',
  earlyaccess  : '/Rogue%20App%20Dashboard/Inbox/early-access_klvwbo.svg',
  hotdeal      : '/Rogue%20App%20Dashboard/Inbox/hotdeal_glgjaa.svg',
  hotdeals     : '/Rogue%20App%20Dashboard/Inbox/hotdeals_okblts.svg',
}

const thumbnail = ref<string | null>(null)

const lowerCaseLabel = computed(() => (props.item.label || '').toLowerCase())

async function loadProduct() {
  let product = await productStore.load(props.item.productId!, rogue)
  if (!product?.thumbnail) {
    product = await productStore.findParent(props.item.productId!, rogue)
  }

  thumbnail.value = product.thumbnail
}

function open() {
  const { email, orderId, type, url, screen, tab, rssToken } = props.item
  if (screen && rogue.isApp('3.3.7')) {
    rogue.emitToApp(messages.OPEN_SCREEN, { screen, tab })
  } else if (url) {
    rogue.emitToApp(messages.OPEN_IN_WEBVIEW, { url })
  } else if (type === 'rss') {
    rogue.emitToApp(messages.OPEN_IN_WEBVIEW, { url: `/shippingtracker?token=${encodeURIComponent(rssToken || '')}` })
  } else if (type === 'order') {
    rogue.emitToApp(messages.OPEN_IN_WEBVIEW, { url: `/account/order/${orderId}` })
  }

  // @TODO: Implement GA (if needed)
  if (!props.item.read) {
    emit('open', props.item)
  }
}

function getNotificationDate(timestamp: string) {
  const d = getDateObjectFromTimestamp(timestamp)
  const localeStringOptions: Intl.DateTimeFormatOptions = {
    year  : 'numeric',
    month : 'numeric',
    day   : 'numeric',
  }

  return d?.toLocaleString(navigator.language, localeStringOptions)
}

onMounted(() => {
  if (props.item.productId) {
    loadProduct()
  } else {
    thumbnail.value = props.item.image || null
  }
})
</script>

<style scoped lang="scss">
.native-app-list-item {
  padding: $space-xxs $space-xs;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.item-thumbnail {
  width: 60px;
  height: 60px;
  margin-right: $space-xxs;
  flex-shrink: 0;
}
.details {
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  padding: 2px 0;
  max-width: calc(100% - (60px + #{$space-xxs}));
}
.header:not(:last-child) {
  margin-bottom: $space-xxxs;
}
.item-date {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  align-self: flex-start;
}
.item-unread {
  display: inline-block;
  width: $space-xxs;
  height: $space-xxs;
  background: #4990E2;
  border-radius: 50%;
  margin-left: $space-xxxs;
}
.item-label {
  font-weight: 600;
  font-family: $font-condensed;
  font-size: $size-xs;
  font-style: italic;
  text-transform: uppercase;
  align-self: center;
}
.media-wrap.item-label {
  height: $size-xs;
  width: 50%;

  &:deep(img) {
    max-height: $size-xxs;
    width: auto;
  }
}
.item-description {
  font-size: $size-xs;
}
</style>
